import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatReceivedAmount,
  useDebounce,
  useDepositData,
} from '@/shared/lib';
import { getDepositFiatCurrencyInputOptions } from '@/shared/lib/deposit';
import { COMMISSION_CHECK_DELAY } from '@/shared/config';
import {
  useCreateDepositOperation,
  useDepositCommission,
  useSbpPaymentType,
} from '@/shared/api';
import { useDepositForm } from './useDepositForm.ts';
import type {
  DepositCommissionResponse,
  DepositFormLogicOptions,
  DepositFormLogicReturn,
  DepositFormValues,
} from '@/shared/types';
import Big from 'big.js';

export const useDepositFormLogic = <TResponse = unknown>({
  operationType,
  onSubmit,
  initialData,
  onCreateDepositSuccess,
  onCreateDepositError,
}: DepositFormLogicOptions<
  DepositFormValues,
  TResponse
>): DepositFormLogicReturn<DepositFormValues> => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<DepositFormValues | null>(
    initialData || null
  );

  const {
    accountNumber,
    depositWithdrawAssetsData,
    assetsIconsData,
    banksData,
    firstDepositDataError,
    hasDataError,
    isDepositDataLoading,
    isAccountInfoError,
    accountsInfoError,
  } = useDepositData();

  const [currentMinAmount, setCurrentMinAmount] = useState(0);
  const [currentMaxAmount, setCurrentMaxAmount] = useState<number>();
  const [currentCurrencyCode, setCurrentCurrencyCode] = useState('');
  const { form: initialForm } = useDepositForm({
    operationType,
    initialData: formData,
  });

  const watchedValues = initialForm.watch();

  const currencyOptions = useMemo(() => {
    return getDepositFiatCurrencyInputOptions(
      operationType,
      depositWithdrawAssetsData?.value,
      assetsIconsData?.value,
      banksData?.value
    );
  }, [operationType, depositWithdrawAssetsData, assetsIconsData, banksData]);

  const { form } = useDepositForm({
    operationType,
    minAmount: currentMinAmount,
    maxAmount: currentMaxAmount,
    currencyCode: initialData?.currencyCode ?? currentCurrencyCode,
    initialData: {
      currencyCode: initialData?.currencyCode ?? currentCurrencyCode,
      amount: watchedValues.amount,
    },
  });

  const actualWatchedValues = form.watch();
  const minAmount = useMemo(() => {
    if (!actualWatchedValues.currencyCode) {
      return 0;
    }

    const selectedOption = currencyOptions.find(
      o => o.value === actualWatchedValues.currencyCode
    );

    return selectedOption?.min || 0;
  }, [actualWatchedValues.currencyCode, currencyOptions]);

  const maxAmount = useMemo(
    () =>
      currencyOptions.find(o => o.value === actualWatchedValues.currencyCode)
        ?.max,
    [actualWatchedValues.currencyCode, currencyOptions]
  );

  useEffect(() => {
    setCurrentMinAmount(minAmount);
    setCurrentMaxAmount(maxAmount);
    setCurrentCurrencyCode(actualWatchedValues.currencyCode);
  }, [minAmount, maxAmount, actualWatchedValues.currencyCode]);

  const {
    mutate: createDeposit,
    isCreateOperationDepositError,
    createOperationDepositError,
    isCreateOperationDepositLoading,
  } = useCreateDepositOperation<TResponse>({
    operationType,
    onSuccess: onCreateDepositSuccess,
    onError: onCreateDepositError,
  });

  const isSbp = operationType === 'sbp';
  const {
    paymentTypeCode,
    isPaymentTypeFetching,
    isPaymentTypeError,
    paymentTypeError,
  } = useSbpPaymentType({
    direction: 'deposit',
    currencyCode: actualWatchedValues.currencyCode,
    enabled: isSbp && !!actualWatchedValues.currencyCode,
  });
  const isPaymentTypeReady = !isSbp || !!paymentTypeCode;

  const debouncedAmount = useDebounce(
    Number(actualWatchedValues.amount),
    COMMISSION_CHECK_DELAY
  );

  const normalizedAmount = Number(actualWatchedValues.amount);

  const hasCommissionInputs = Boolean(
    actualWatchedValues.currencyCode &&
      !Number.isNaN(normalizedAmount) &&
      normalizedAmount > 0
  );

  const hasFormErrors = Object.keys(form.formState.errors).length > 0;

  const isCommissionEnabled = !!(
    !hasFormErrors &&
    actualWatchedValues.currencyCode &&
    !Number.isNaN(debouncedAmount) &&
    normalizedAmount > 0 &&
    isPaymentTypeReady
  );

  const {
    data: commissionData,
    isLoading: isCommissionFetching,
    error: commissionError,
  } = useDepositCommission<
    DepositCommissionResponse,
    Record<string, string | number>
  >({
    operationType,
    params: {
      currencyCode: actualWatchedValues.currencyCode,
      amount: debouncedAmount,
      ...(isSbp && paymentTypeCode ? { paymentTypeCode } : {}),
    },
    enabled: isCommissionEnabled,
  });

  const isAmountDebouncing =
    hasCommissionInputs &&
    Number(actualWatchedValues.amount) !== debouncedAmount;

  const isCommissionPending =
    hasCommissionInputs && (isAmountDebouncing || isCommissionFetching);

  const shouldShowCommission =
    !hasFormErrors &&
    hasCommissionInputs &&
    !isCommissionPending &&
    commissionData &&
    !commissionError;

  const isSubmitInProgressWithoutErrors =
    (isCommissionPending && !(commissionError || hasFormErrors)) ||
    (isSbp && isPaymentTypeFetching) ||
    isCreateOperationDepositLoading;

  const formError: unknown | undefined =
    (isSbp && paymentTypeError) || commissionError;

  const handleFormSubmit = (values: DepositFormValues) => {
    if (onSubmit) {
      onSubmit(values);

      return;
    }

    if (
      hasFormErrors ||
      Boolean(commissionError) ||
      isSubmitInProgressWithoutErrors ||
      !isPaymentTypeReady ||
      isPaymentTypeError
    ) {
      return;
    }

    setFormData(values);
    createDeposit({
      currencyCode: values.currencyCode,
      amount: Number(values.amount),
      accountNumber,
      ...(isSbp && paymentTypeCode ? { paymentTypeCode } : {}),
    } as unknown as Parameters<typeof createDeposit>[0]);
  };

  const amountPlaceholder = actualWatchedValues.currencyCode
    ? t('common.amountMinAmountPlaceholder', {
        min: formatReceivedAmount(minAmount),
        currency: actualWatchedValues.currencyCode,
      })
    : t('common.amountPlaceholder');

  const isCommissionData = commissionData && typeof commissionData === 'object';

  return {
    // Form
    form,
    watchedValues: actualWatchedValues,
    handleFormSubmit,
    hasFormErrors,
    createDeposit,

    // Data
    currencyOptions,
    minAmount,
    amountPlaceholder,
    accountNumber,

    // Commission
    commissionData:
      isCommissionData &&
      commissionData &&
      typeof commissionData === 'object' &&
      'feeAmount' in commissionData
        ? {
            feeAmount: (commissionData as DepositCommissionResponse).feeAmount,
            currencyCode: actualWatchedValues.currencyCode,
            receiveAmount: new Big(debouncedAmount)
              .minus(commissionData?.feeAmount || 0)
              .toNumber(),
          }
        : undefined,
    isCommissionFetching,
    commissionError:
      isCommissionEnabled && !isCommissionFetching && !isCommissionData
        ? new Error('common.unknownError')
        : commissionError,
    shouldShowCommission: !!shouldShowCommission,
    isCommissionPending,

    // States
    isSubmitInProgressWithoutErrors,
    formError,

    // Loading states
    isLoading: isDepositDataLoading,
    isAccountInfoError,
    accountsInfoError,

    // Error states
    hasDataError: isCreateOperationDepositError || hasDataError,
    firstDepositDataError,

    isCreateOperationDepositError,
    createOperationDepositError,

    openDepositLink: (url: string, onBack: () => void) => {
      if (url) {
        window.open(url, '_blank');
      }
      onBack();
    },
  };
};
